import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ApprovalService from "../services/ApprovalService";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import RefreshingIndicator from "../components/RefreshingIndicator";
import UserService from "../services/UserService";

import VisibilityIcon from "@mui/icons-material/Visibility";
import { JsonSyntaxHighlighter } from "../components/JsonViewer";

export default function Approvals() {
  const queryClient = useQueryClient();
  const [decisionDlgOpen, setDecisionDlgOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisionType, setDecisionType] = useState(null); // 'APPROVE' or 'REJECT'
  const [comments, setComments] = useState("");
  const canDecide = UserService.hasRole(["ap-decide"]);
  
  const [contextDlgOpen, setContextDlgOpen] = useState(false);
  const [contextData, setContextData] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);

  const { data, isLoading, isFetching, isError } = useQuery(
    ["approvals"],
    () => ApprovalService.getApprovals("PENDING", null) // Fetch pending by default
  );

  const mutation = useMutation(
    (payload) => ApprovalService.decideRequest(payload.id, payload.decision),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["approvals"]);
        handleCloseDlg();
        handleCloseContext(); // Also close context dialog if open
      },
    }
  );

  const handleOpenDlg = (row, type) => {
    setSelectedRequest(row);
    setDecisionType(type);
    setComments("");
    setDecisionDlgOpen(true);
  };

  const handleCloseDlg = () => {
    setDecisionDlgOpen(false);
    setSelectedRequest(null);
    setDecisionType(null);
  };
  
  const handleOpenContext = async (row) => {
      setContextDlgOpen(true);
      setContextLoading(true);
      setContextData(null);
      setSelectedRequest(row); // Store row for decision actions
      try {
          const response = await ApprovalService.getApprovalContext(row.id);
          setContextData(response.data);
      } catch (error) {
          console.error("Failed to fetch context", error);
      } finally {
          setContextLoading(false);
      }
  };
  
  const handleCloseContext = () => {
      setContextDlgOpen(false);
      setContextData(null);
  };

  const handleSubmitDecision = () => {
    mutation.mutate({
      id: selectedRequest.id,
      decision: {
        approved: decisionType === "APPROVE",
        comments: comments,
      },
    });
  };

  const columns = [
    { field: "processType", headerName: "Type", width: 150 },
    { field: "status", headerName: "Status", width: 120 },
    { 
        field: "idUser", 
        headerName: "Requester", 
        width: 200,
        valueGetter: (params) => params.row.idUser
    },
    { 
        field: "createdAt", 
        headerName: "Created At", 
        width: 200,
        valueFormatter: (params) => new Date(params.value).toLocaleString()
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => {
        return (
        <Box>
           <Tooltip title="View Context">
            <Button color="primary" onClick={() => handleOpenContext(params.row)}>
              <VisibilityIcon />
            </Button>
          </Tooltip>
          {canDecide && (
            <>
            <Tooltip title="Approve">
                <Button color="success" onClick={() => handleOpenDlg(params.row, "APPROVE")}>
                <CheckIcon />
                </Button>
            </Tooltip>
            <Tooltip title="Reject">
                <Button color="error" onClick={() => handleOpenDlg(params.row, "REJECT")}>
                <CloseIcon />
                </Button>
            </Tooltip>
            </>
          )}
        </Box>
      )},
    },
  ];

  return (
    <Box sx={{ height: "calc(100vh - 100px)", width: "100%", p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Pending Approvals
        </Typography>
        {isFetching && <RefreshingIndicator />}
      </Box>

      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Typography color="error">Error fetching approvals.</Typography>
      ) : (
        <DataGrid
          rows={data?.data || []}
          columns={columns}
          getRowId={(row) => row.id}
          components={{ Toolbar: GridToolbar }}
          disableSelectionOnClick
        />
      )}

      <Dialog open={decisionDlgOpen} onClose={handleCloseDlg}>
        <DialogTitle>
          {decisionType === "APPROVE" ? "Approve Request" : "Reject Request"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {decisionType === "APPROVE" ? "approve" : "reject"} this request?
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="comments"
            label="Comments"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDlg}>Cancel</Button>
          <Button onClick={handleSubmitDecision} variant="contained" color={decisionType === "APPROVE" ? "success" : "error"}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={contextDlgOpen} onClose={handleCloseContext} fullWidth maxWidth="sm">
        <DialogTitle>Request Context</DialogTitle>
        <DialogContent>
            {contextLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : contextData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                     <Box>
                        <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                        <Typography variant="body1">{contextData.processType}</Typography>
                    </Box>
                    {contextData.employerName && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Employer</Typography>
                            <Typography variant="body1">{contextData.employerName}</Typography>
                        </Box>
                    )}
                    {contextData.locationName && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                            <Typography variant="body1">{contextData.locationName}</Typography>
                        </Box>
                    )}
                    {contextData.customerName && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                            <Typography variant="body1">{contextData.customerName}</Typography>
                             {contextData.customerEmail && (
                                <Typography variant="body2" color="text.secondary">{contextData.customerEmail}</Typography>
                            )}
                        </Box>
                    )}
                    {contextData.userName && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Requester Details</Typography>
                            <Typography variant="body1">{contextData.userName}</Typography>
                        </Box>
                    )}
                     <Box>
                        <Typography variant="subtitle2" color="text.secondary">Full Details</Typography>
                        <Box sx={{
                            maxHeight: "40vh",
                            overflow: "auto",
                            p: 2,
                            backgroundColor: "#fff",
                            border: "1px solid #ddd",
                            mt: 1,
                            borderRadius: 1,
                        }}>
                            <JsonSyntaxHighlighter 
                                jsonString={JSON.stringify(contextData.requestDetails, null, 2)} 
                                compact={false} 
                            />
                        </Box>
                    </Box>
                </Box>
            ) : (
                <Typography>No context data available.</Typography>
            )}
        </DialogContent>
        <DialogActions>
             {canDecide && contextData && (
                <>
                    <Button color="success" onClick={() => handleOpenDlg(selectedRequest, "APPROVE")}>
                        Approve
                    </Button>
                    <Button color="error" onClick={() => handleOpenDlg(selectedRequest, "REJECT")}>
                        Reject
                    </Button>
                </>
            )}
            <Button onClick={handleCloseContext}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
